import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Column } from "@tanstack/react-table";

import { DatasetRow } from "src/api/types";
import { useDatasetEditTableActions } from "src/datasets/DatasetTable/stores";
import { ValidationResult } from "src/datasets/DatasetTable/validators";
import { CellWrapper } from "src/datasets/components/CellWrapper";
import {
  ButtonItem,
  ControllableDropdown,
} from "src/datasets/components/ControllableDropdown";
import { JSONEditorCell } from "src/datasets/components/JSONEditorCell";
import {
  ErrorDetails,
  getErrorDetails,
} from "src/datasets/components/errorUtils";
import { useShowDefaultEditor } from "src/datasets/components/useShowDefaultEditor";
import { createCaseDataTemplate } from "src/datasets/utils";
import { CaseSchemaWithMetadata } from "src/schema/schemaEditor/CaseSchemaEditor";

export const CaseCell: React.FC<{
  isOpen: boolean;
  column: Column<DatasetRow>;
  disabled: boolean;
  error: ValidationResult | undefined;
  value: string;
  onChange: (value: string) => void;
  warning: boolean;
  editing: boolean;
  selected: boolean;
  rowIndex: number;
}> = ({
  isOpen,
  column,
  disabled,
  error,
  value,
  onChange,
  warning,
  editing,
  selected,
  rowIndex,
}) => {
  const { reset } = useDatasetEditTableActions();
  const [showDefaultEditor, setShowDefaultEditor] = useShowDefaultEditor(
    editing,
    value,
  );
  const resource = column.columnDef.meta?.resource as CaseSchemaWithMetadata;

  if (!resource || showDefaultEditor) {
    return (
      <JSONEditorCell
        column={column}
        disabled={disabled}
        editing={editing}
        invalid={!!(error && value)}
        isOpen={editing}
        rowIndex={rowIndex}
        selected={selected}
        type="object"
        value={value}
        warning={warning}
        onChange={onChange}
        onClose={() => reset("editingCellId")}
      />
    );
  }

  return (
    <CaseRenderer
      editing={editing}
      error={getErrorDetails(error, value, onChange)}
      isOpen={isOpen}
      selected={selected}
      onChange={() => {
        const caseData = createCaseDataTemplate(resource);
        onChange(JSON.stringify(caseData));
        setShowDefaultEditor();
      }}
      onNewValue={setShowDefaultEditor}
    />
  );
};

const CaseRenderer: React.FC<{
  isOpen: boolean;
  onNewValue: () => void;
  onChange: () => void;
  error: ErrorDetails | undefined;
  editing: boolean;
  selected: boolean;
}> = ({ isOpen, onNewValue, onChange, error, editing, selected }) => {
  return (
    <CellWrapper editing={editing} error={error} selected={selected}>
      <ControllableDropdown isOpen={isOpen} width="w-96">
        <ButtonItem icon={faPlus} label="New value" onClick={onNewValue} />
        <ButtonItem
          icon={faPlus}
          label="Empty case template"
          onClick={onChange}
        />
      </ControllableDropdown>
    </CellWrapper>
  );
};
